<template>
  <v-app v-if="!loading">
    <RouterView />
    <div v-if="!isOnV2" class="alert-container">
      <v-alert
        v-for="[id, notification] of notificationsStore"
        :key="id"
        closable
        :text="notification.text"
        :type="notification.type"
        @click:close.stop="removeNotification(id)"
      >
        <template #append>
          <v-btn
            v-if="notification.secondaryAction"
            size="small"
            variant="text"
            @click.stop="notification.secondaryAction"
          >
            {{ notification.secondaryActionLabel }}
          </v-btn>
          <v-btn
            v-if="notification.action"
            size="small"
            variant="text"
            @click.stop="notification.action"
          >
            {{ notification.actionLabel }}
          </v-btn>
        </template>
      </v-alert>
    </div>
  </v-app>
</template>

<script setup>
import { onMounted, provide, reactive, readonly, ref, nextTick, computed } from "vue";
import { RouterView, useRouter } from "vue-router";
import {
  languageCodesKey,
  tenantDataKey,
  tenantsListKey,
  translationsKey,
  marketSpecifierTypesKey,
  tenantChildrenKey,
} from "./constants";
import { t } from "@/services/i18n";
import { getActiveFeeds, getActiveFeedTypes, getActiveManualOddsFeeds } from "./services/api/feeds";
import { getTenantDefaultLanguage, getEnabledLanguages } from "./services/api/languages";
import { getSupplementalNameTypes } from "./services/api/supplementalNameTypes";
import { mapLangugageCodes } from "@/services/formatter";

import { getCurrencies } from "@/services/api/bonus.js";
import { getTenantApps, getUserTenants } from "@/services/api/tenants";
import { getMarketsSpecifierTypes } from "@/services/api/markets";
import { setSupplementals } from "@/services/supplementals";

import { tenantStore } from "@/stores/tenant.store";

import {
  addNotification,
  notificationsStore,
  removeNotification,
} from "@/stores/notifications.store.js";
import { getChildTenants } from "@/services/api/tenants";
import { startAllActiveAlarms } from "./components/operations/feed-mapping/common/alarmFeedMappingService";
import { getTenantStreams } from "@/services/api/streams";
import { getResolveFormulaExpressionComponents } from "@/services/api/resolve-formula.js";

const translations = ref({});
const languageCodes = ref({});
const marketSpecifierTypes = ref([]);
const tenantChildren = ref([]);

const tenantsList = ref([]);
const loading = ref(false);
const router = useRouter();

const tenantData = reactive({
  enabledLanguages: [],
  defaultLanguage: {},
  activeFeedTypes: [],
  activeFeeds: [],
  activeOddsFeeds: [],
  supplementalNameTypes: [],
  supportedCurrencies: [],
  applications: [],
  streams: [],
  resolveFormulaExpressionComponents: [],
});

provide(tenantDataKey, readonly(tenantData));
provide(translationsKey, readonly(translations));
provide(languageCodesKey, readonly(languageCodes));
provide(tenantsListKey, tenantsList);
provide(marketSpecifierTypesKey, readonly(marketSpecifierTypes));
provide(tenantChildrenKey, readonly(tenantChildren));

function updateNotificationOptions(data) {
  addNotification(data);
}

provide("notificationOptions", {
  updateNotificationOptions,
});

async function fetchTenants() {
  try {
    const tenants = await getUserTenants();
    tenantsList.value = tenants.data;
    return tenants.data;
  } catch (e) {
    return e;
  }
}

async function setTenantData() {
  loading.value = true;
  if (!tenantsList.value?.length) {
    await fetchTenants();
  }
  if (!window.tenantUuid) {
    const firstAvailableTenant = tenantsList.value[0]?.uuid;

    const currentTenantUuid =
      new URLSearchParams(window.location.search).get("tenantUuid") ||
      localStorage.getItem("tenantUuid");

    const tenantUuidToUse = tenantsList.value.some((tenant) => currentTenantUuid === tenant.uuid)
      ? currentTenantUuid
      : firstAvailableTenant;

    localStorage.setItem("tenantUuid", tenantUuidToUse);
    window.tenantUuid = tenantUuidToUse;
  }

  const tenant = tenantsList.value.find((tenant) => tenant.uuid === window.tenantUuid);
  window.tenantName = tenant.name;
  window.tenantId = tenant.id;

  await Promise.all([
    getEnabledLanguages(),
    getTenantDefaultLanguage(),
    getSupplementalNameTypes(),
    getActiveFeedTypes(),
    getActiveFeeds(),
    () => {},
    getCurrencies(window.tenantUuid),
    getMarketsSpecifierTypes(),
    getActiveManualOddsFeeds(),
    getChildTenants(),
    getTenantApps(),
    getTenantStreams(),
    getResolveFormulaExpressionComponents(),
  ])
    .then((res) => {
      tenantData.enabledLanguages = !res[0].error ? res[0].data : [];
      tenantData.defaultLanguage = !res[1].error ? res[1].data : {};
      setSupplementals(!res[2].error ? res[2].data : []);
      tenantData.activeFeedTypes = !res[3].error ? res[3].data : [];
      tenantData.activeFeeds = !res[4].error ? res[4].data : [];

      const currencies = res[6].data?.data?.map((value) => value.alphaCode);

      tenantData.supportedCurrencies = currencies ?? [];
      tenantData.currenciesData = res[6].data?.data ?? [];
      marketSpecifierTypes.value = res[7].data ?? [];
      tenantData.activeOddsFeeds = !res[8].error ? res[8].data : [];
      tenantChildren.value = res[9]?.data;
      tenantData.applications = res[10].data ?? [];
      tenantData.streams = !res[11].error ? res[11].data?.data || res[11].data : [];
      tenantData.resolveFormulaExpressionComponents = !res[12].error ? res[12].data : {};

      if (tenantData.enabledLanguages.length) {
        languageCodes.value = mapLangugageCodes(tenantData.enabledLanguages);
        tenantStore.languages = tenantData.enabledLanguages;
      }

      loading.value = false;

      if (window.tenantUuid) {
        startAllActiveAlarms();
      }

      return true;
    })
    .catch((error) => {
      console.error(error);
      updateNotificationOptions({
        visible: true,
        type: "error",
        text: t("common.notifications.fetch_error"),
      });
      return error;
    })
    .finally(() => {
      nextTick(async () => {
        if (
          router.currentRoute.value.name !== "liabilityTemplates" ||
          tenantChildren.value?.length
        ) {
          return;
        }

        router.push({ name: "liability" });
      });
    });
}

function resetTenantData() {
  tenantData.enabledLanguages = [];
  tenantData.defaultLanguage = {};
  tenantData.activeFeedTypes = [];
  tenantData.activeFeeds = [];
  tenantData.activeOddsFeeds = [];
  tenantData.supplementalNameTypes = [];
  tenantData.supportedCurrencies = [];
  tenantData.applications = [];

  tenantsList.value = [];
}

provide("setTenantData", setTenantData);
provide("resetTenantData", resetTenantData);

const isOnV2 = computed(() => {
  return router.currentRoute.value.path.includes("v2");
});

onMounted(() => {
  localStorage.setItem("instance", "0");
  localStorage.setItem("language", "en");

  setTenantData();
});
</script>

<style lang="scss">
:root {
  --v-theme-on-surface: 123, 123, 123;
}

// Remove this once filter component is created
.filters {
  gap: 2rem;

  & > div:first-child {
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  & > div:last-child {
    align-self: flex-end;
  }

  &__status {
    .v-field__input {
      min-width: 80px;
    }
  }

  &__live-status,
  &__language {
    .v-field__input {
      min-width: 110px;
    }
  }

  &__prematch-status,
  &__games {
    .v-field__input {
      min-width: 160px;
    }
  }

  &__feeds {
    .v-field__input {
      min-width: 170px;
    }
  }
}

.v-menu {
  & > .v-overlay__content {
    & > .v-list {
      overflow-x: overlay !important;

      &::-webkit-scrollbar {
        width: 6px;
        background: transparent;
        border-radius: 0.25rem;
        opacity: 0;
        transition: all 0.3s ease;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(#ccc, 0.3);
        border-radius: 0.25rem;
        transition: all 1.3s ease;
      }

      &:hover::-webkit-scrollbar-thumb {
        background: rgba(#aaa, 0.5);
      }
    }
  }
}

.alert-container {
  position: fixed;
  z-index: 2450;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 30vw;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.v-alert__append {
  display: flex;
  align-items: center;
  height: 100%;
}

.v-navigation-drawer__scrim {
  opacity: 0.5 !important;
}
</style>
